import './lib/rpc';
import './public/maskable-512x512.png';
import './public/ourliving-192x192.png';
import './public/ourliving-512x512.png';
import './public/OurLiving.png';
import ReactDOM from 'react-dom';
import React, { useEffect, useState } from 'react';
import { Routes } from './navigation';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import queryConfig from './reactQuery/reactQueryConfig';
import { ThemeProvider } from 'emotion-theming';
import { style } from './components/Shared/Style';
import { Global, css } from '@emotion/core';
import ContentArea from './components/ContentArea';
import ModalContext from '@app/web/components/animated-modal/Context';
import { SocketStore } from './lib/socketStore';
import useGetTheme from './components/Shared/hooks/useGetTheme';
import { ThemeType } from '@app/api/models/Theme';
import { colors } from './components/Shared/Style/colors';
import { isCordovaBuild } from './lib/cordovaHelpers';
// import { useI18Theme } from './lib/useI18Theme';
import toast, { Toaster } from 'react-hot-toast';
import { desktop } from './components/MediaQuery/breakpoints';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import * as Sentry from '@sentry/react';
import sentryConfig from './sentry/sentryConfig';

const config = sentryConfig();
Sentry.init({
    dsn: config.SENTRY_DSN,
    environment: config.SENTRY_ENVIRONMENT,
    tracesSampleRate: 0.1,
});

CapacitorUpdater.notifyAppReady();

CapacitorUpdater.addListener('download', ({ percent }) => {
    toast.loading(
        <div>
            <p>Laddar ner de senste uppdateringarna, {percent} / 100%</p>
            <p>Stäng inte av appen medan nedladdningen pågår</p>
        </div>,
        { id: 'codepush', duration: Infinity }
    );
});

CapacitorUpdater.addListener('downloadFailed', (error) => {
    Sentry.captureException(error, { data: 'downloadFailed'});
});

CapacitorUpdater.addListener('updateFailed', (error) => {
    Sentry.captureException(error, { data: 'updateFailed'});
});

CapacitorUpdater.addListener('downloadComplete', () => {
    toast.success(
        <div>
            <p>Nedladdningen har slutförts!</p>
            <p>Starta om appen för att få de senaste uppdateringarna</p>
        </div>,
        {
            id: 'codepush',
            duration: Infinity,
        }
    );
});

CapacitorUpdater.addListener('appReloaded', () => {
    toast.loading(
        (currentToast) => (
            <div onClick={() => toast.dismiss(currentToast.id)}>Senaste uppdateringen är installerad</div>
        ),
        { id: 'codepush', duration: Infinity }
    );
});

SocketStore;

const App = () => {
    const { data: themeData } = useGetTheme();
    const [theme, setTheme] = useState<ThemeType>({
        colors: style.colors,
    });
    useEffect(() => {
        if (!themeData) return;
        setTheme({
            colors: { ...style.colors, ...themeData.colors },
            globalCss: themeData.globalCss,
        });
    }, [themeData]);

    useEffect(() => {
        window.sessionStorage.setItem('retry-error-refreshed', 'false');
    }, []);
    return (
        <>
            <Global
                styles={css`
                    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap');
                    body {
                        margin: 0;
                        background-color: ${colors.pageBackground};
                        font-family: DM sans;
                        box-sizing: border-box;
                        min-width: 320px;
                    }
                    body * {
                        box-sizing: inherit;
                        font-family: inherit;
                        font-size: inherit;
                    }
                    ul {
                        padding: 0;
                        margin: 0;
                    }
                    li {
                        list-style-type: none;
                    }
                    html {
                        @media screen and (min-width: ${desktop}px) {
                            overflow-x: hidden;
                            margin-right: calc(-1 * (100vw - 100%));
                        }
                    }
                    a {
                        text-decoration: none;
                        color: inherit;
                    }
                `}
            />
            <Global
                styles={css`
                    ${theme?.globalCss}
                `}
            />
            <ThemeProvider theme={theme}>
                <div>
                    <Toaster />
                </div>
                <ContentArea>
                    <ModalContext>
                        <Routes />
                    </ModalContext>
                </ContentArea>
            </ThemeProvider>
        </>
    );
};

const queryClient = new QueryClient(queryConfig);
const AppProviders = () => {
    return (
        <BrowserRouter>
            <QueryClientProvider client={queryClient}>
                <App />
                <ReactQueryDevtools initialIsOpen={false} position={'bottom-left'} />
            </QueryClientProvider>
        </BrowserRouter>
    );
};

const startApp = () => {
    const app = document.createElement('div');
    document.body.appendChild(app);
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    document.title = 'Boende';

    meta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');
    document.getElementsByTagName('head')[0].appendChild(meta);

    ReactDOM.render(<AppProviders />, app);
};

if (isCordovaBuild) {
    const onDeviceReady = () => {
        startApp();
    };
    document.addEventListener('deviceready', onDeviceReady, false);
} else {
    startApp();
}
